import { useEffect, useState } from 'react';
import { Eye, EyeOff, CheckCircle, Plus, Loader2, AlertTriangle, Check, Lightbulb, PenLine } from 'lucide-react';
import api from '../../services/api';

/**
 * Bibliothèque de sujets d'exemple de l'essai gratuit (backend/data/trial-examples, relus) :
 * onglets de domaine (celui de la personne présélectionné) → cartes d'exemple → aperçu de 3 questions
 * → « Ajouter à ma thématique » (sans IA, sans consommer la génération d'essai, chaque exemple une fois).
 * Les exemples déjà ajoutés restent affichés, marqués. onAdded({ thematiqueId, slug, sousSujetId }).
 * L'intérêt est de montrer « cette idée → ces questions » : l'aperçu commence par l'idée saisie, et
 * onUseIdea({ label, consignes }) (facultatif) permet de repartir de cette idée pour générer la sienne.
 */
export default function TrialExamplesPicker({ title = 'Voir d’abord un exemple de résultat', disabled = false, onAdded, onUseIdea = null }) {
    const [examples, setExamples] = useState([]);
    const [domains, setDomains] = useState([]);
    const [domain, setDomain] = useState(null);
    const [openEx, setOpenEx] = useState(null);
    const [adding, setAdding] = useState(null); // clé en cours d'ajout
    const [error, setError] = useState('');

    const load = () => api.get('/custom/trial-examples.php')
        .then(({ data }) => {
            const d = data.data || {};
            setExamples(d.examples || []);
            setDomains(d.domains || []);
            // Présélection une seule fois ; ensuite on respecte le choix de la personne.
            setDomain(cur => (cur && (d.domains || []).some(x => x.key === cur)) ? cur : (d.default_domain || null));
        })
        .catch(() => {});
    useEffect(() => { load(); }, []);

    const addExample = async (key) => {
        setAdding(key);
        setError('');
        try {
            const { data } = await api.post('/custom/trial-examples.php', { key });
            setOpenEx(null);
            load();
            onAdded?.({ thematiqueId: data.data.thematique_id, slug: data.data.slug, sousSujetId: data.data.sous_sujet_id });
        } catch (err) {
            setError(err.response?.data?.message || 'L’exemple n’a pas pu être ajouté. Réessayez dans un instant.');
        } finally {
            setAdding(null);
        }
    };

    if (!examples.length) return null;
    const visible = domain ? examples.filter(x => x.domain === domain) : examples;

    return (
        <section className="trial-lib" aria-label="Sujets d’exemple">
            <h4 className="trial-lib__title">{title}</h4>
            {domains.length > 1 && (
                // Mini-menu au rendu des thématiques (bandeau de l'en-tête) : image et couleur de la thématique éditoriale.
                <div className="trial-lib__scopes" role="tablist" aria-label="Domaine">
                    {domains.map(d => (
                        <button key={d.key} type="button" role="tab" aria-selected={domain === d.key}
                            className={`header-scope-pill ${d.image ? 'header-scope-pill--bg' : ''} ${domain === d.key ? 'active' : ''}`}
                            style={{
                                ...(d.color ? { '--scope-color': d.color } : {}),
                                ...(d.image ? { backgroundImage: `url(${d.image})` } : {}),
                            }}
                            onClick={() => { setDomain(d.key); setOpenEx(null); }} disabled={disabled || !!adding}>
                            <span>{d.label}</span>
                        </button>
                    ))}
                </div>
            )}
            {error && <p className="trial-card__error"><AlertTriangle size={14} /> {error}</p>}
            <div className="trial-lib__list">
                {visible.map(ex => {
                    const open = openEx === ex.key;
                    return (
                        <article key={ex.key} className={`trial-lib__item${open ? ' is-open' : ''}${ex.added ? ' is-added' : ''}`}>
                            <div className="trial-lib__head">
                                <div className="trial-lib__info">
                                    <strong>{ex.label}</strong>
                                    <span>Idée : « {ex.consignes} »</span>
                                </div>
                                <div className="trial-lib__btns">
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpenEx(open ? null : ex.key)} disabled={disabled}>
                                        {open ? <EyeOff size={14} /> : <Eye size={14} />} {open ? 'Fermer' : 'Aperçu'}
                                    </button>
                                    {ex.added ? (
                                        <span className="trial-lib__added"><Check size={14} /> Dans votre thématique</span>
                                    ) : (
                                        <button type="button" className="btn btn-primary btn-sm" onClick={() => addExample(ex.key)} disabled={disabled || !!adding}>
                                            {adding === ex.key ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Ajouter
                                        </button>
                                    )}
                                </div>
                            </div>
                            {open && (
                                <div className="trial-ex">
                                    <div className="trial-ex__idea">
                                        <span className="trial-ex__idea-label"><Lightbulb size={13} /> Idée saisie dans « Depuis une idée »</span>
                                        <p>{ex.consignes}</p>
                                        {onUseIdea && (
                                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setOpenEx(null); onUseIdea({ label: ex.label, consignes: ex.consignes }); }}>
                                                <PenLine size={13} /> Partir de cette idée
                                            </button>
                                        )}
                                    </div>
                                    <p className="trial-ex__intro">Résultat : {ex.nb_questions} questions générées par l’IA puis relues. En voici 3 :</p>
                                    {ex.preview.map((q, i) => (
                                        <div key={i} className="trial-ex__q">
                                            <p className="trial-ex__enonce">{q.enonce}</p>
                                            <ul className="trial-ex__reps">
                                                {Object.entries(q.reponses).map(([k, v]) => (
                                                    <li key={k} className={k === q.bonne_reponse ? 'is-good' : ''}>
                                                        {k === q.bonne_reponse && <CheckCircle size={13} />} {v}
                                                    </li>
                                                ))}
                                            </ul>
                                            <div className="trial-ex__expl">
                                                {String(q.explication).split(/\n\s*\n/).map((para, j) => (
                                                    // Contenu relu, stocké côté serveur : seules les balises <strong> sont conservées.
                                                    <p key={j} dangerouslySetInnerHTML={{ __html: para.replace(/<(?!\/?strong>)[^>]*>/g, '') }} />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {!ex.added && (
                                        <p className="trial-ex__note">L’ajout ne consomme pas votre génération gratuite : vous pourrez ensuite créer votre propre sujet.</p>
                                    )}
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>
        </section>
    );
}
